import React from "react";
import styled from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";
import SimflexityLogo from "images/icons/svg/v2/simflexity-logo.svg";
import { useTranslation } from "react-i18next";

export interface MenuItem {
  value: string;
  label: string;
  iconName: string;
  href: string;
}

interface MenuProps {
  items: MenuItem[];
  activeValue: string;
  logoSrc?: string;
  onItemClick?: (
    item: MenuItem,
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => void;
}

/**
 * Top navigation menu with logo and selectable items.
 */
export const NavbarMenu = ({
  items,
  activeValue,
  logoSrc = SimflexityLogo,
  onItemClick,
}: MenuProps): React.ReactElement => {
  const { t } = useTranslation();

  const handleItemClick = (
    item: MenuItem,
    event: React.MouseEvent<HTMLAnchorElement>,
  ): void => {
    onItemClick?.(item, event);
  };

  return (
    <Container role="navigation" aria-label={t("DesignComponents.MainMenu")}>
      <Logo src={logoSrc} alt="Simflexity logo" />
      <Items>
        {items.map((item) => {
          const isActive = item.value === activeValue;
          return (
            <ItemLink
              key={item.value}
              href={item.href}
              aria-current={isActive ? "page" : undefined}
              $isActive={isActive}
              onClick={(event) => handleItemClick(item, event)}
            >
              <CustomIcon
                name={item.iconName}
                size="24px"
                color={isActive ? ColorV2.Blue1 : ColorV2.Blue5}
              />
              <Label $isActive={isActive}>{item.label}</Label>
            </ItemLink>
          );
        })}
      </Items>
    </Container>
  );
};

const Container = styled.nav`
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 24px;
  border-radius: 24px;
  background-color: ${ColorV2.White};
  width: 100%;
`;

const Logo = styled.img`
  width: 128px;
  height: 56px;
  object-fit: contain;
`;

const Items = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: flex-end;
  gap: clamp(24px, 6vw, 100px);
  margin-left: clamp(32px, 8vw, 98px);
`;

const ItemLink = styled.a<{ $isActive: boolean }>`
  ${TypographyV2.b1}
  display: inline-flex;
  align-items: center;
  gap: ${(props) => (props.$isActive ? "12px" : "0")};
  color: ${(props) => (props.$isActive ? ColorV2.Blue1 : ColorV2.Blue5)};
  text-decoration: none;
  transition:
    gap 0.2s ease,
    color 0.2s ease;
`;

const Label = styled.span<{ $isActive: boolean }>`
  ${TypographyV2.b1}
  color: inherit;
  max-width: ${(props) => (props.$isActive ? "180px" : "0")};
  opacity: ${(props) => (props.$isActive ? 1 : 0)};
  transform: ${(props) =>
    props.$isActive ? "translateX(0)" : "translateX(-6px)"};
  overflow: hidden;
  white-space: nowrap;
  transition:
    max-width 0.2s ease,
    opacity 0.2s ease,
    transform 0.2s ease;
`;
